import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Building2, Users, Plus, Edit, Trash2, Eye, RefreshCw, 
  Clock, CheckCircle, AlertTriangle, XCircle, DollarSign,
  MapPin, Phone, Mail, FileText, TrendingUp, Calendar, Receipt,
  Settings, Zap, PlusCircle, X, Upload, FileUp, Percent, Download,
  Key, Send, Search, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import VendorInvoices from './VendorInvoices';

const ONBOARDING_STATUSES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  documents_pending: { label: 'Docs Pending', color: 'bg-orange-100 text-orange-800' },
  verified: { label: 'Verified', color: 'bg-blue-100 text-blue-800' },
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-800' }
};

const CHECK_TYPES = [
  { id: 'id_check', name: 'ID Verification' },
  { id: 'criminal_court_check', name: 'Criminal Court Check' },
  { id: 'court_advocate_check', name: 'Court Advocate Check' },
  { id: 'address_check', name: 'Address Verification' },
  { id: 'employment_check', name: 'Employment Verification' },
  { id: 'education_check', name: 'Education Verification' },
  { id: 'reference_check', name: 'Reference Check' },
  { id: 'credit_check', name: 'Credit Check' },
  { id: 'drug_test', name: 'Drug Test' },
  { id: 'world_criminal_db', name: 'World Database Check' },
  { id: 'india_criminal_db', name: 'India Criminal Database' }
];

const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam',
  'Bihar', 'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli', 'Daman and Diu',
  'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh',
  'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala',
  'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

export default function VendorTab() {
  const [activeTab, setActiveTab] = useState('vendors');
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [showVendorPricingDialog, setShowVendorPricingDialog] = useState(false);
  const [showTatDialog, setShowTatDialog] = useState(false);
  const [showContactsDialog, setShowContactsDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showPenaltyDialog, setShowPenaltyDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorSort, setVendorSort] = useState('name_asc');
  const [selectedVendorForPricing, setSelectedVendorForPricing] = useState(null);
  const [selectedVendorForTat, setSelectedVendorForTat] = useState(null);
  const [selectedVendorForContacts, setSelectedVendorForContacts] = useState(null);
  const [selectedVendorForPenalty, setSelectedVendorForPenalty] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [bulkImportData, setBulkImportData] = useState([]);
  const [bulkImportErrors, setBulkImportErrors] = useState([]);
  const [sendVendorInvites, setSendVendorInvites] = useState(true);

  const [vendorForm, setVendorForm] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    gst_number: '',
    pan_number: '',
    specializations: [],
    service_locations: [],
    default_tat_days: 7,
    pricing: [],
    coverage_type: 'state_specific',
    coverage_states: [],
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: '',
    agreement_file: null,
    agreement_validity: '',
    tat_penalties: [
      { period_start: 6, period_end: 10, penalty_percent: 5 },
      { period_start: 11, period_end: 15, penalty_percent: 10 },
      { period_start: 16, period_end: 20, penalty_percent: 15 },
      { period_start: 21, period_end: null, penalty_percent: 20 }
    ],
    // New password and invite fields
    password: '',
    send_invite: true
  });

  const [pricingForm, setPricingForm] = useState({
    check_type: '',
    state: 'default',
    city: '',
    price: '',
    tat_days: 7
  });
  // May 24, 2026 — when set, the Add Pricing dialog is in EDIT mode
  // and saving the form will UPDATE the existing row identified by
  // {check_type, original_location} rather than appending a new one.
  const [editingPricingRow, setEditingPricingRow] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use same API as Operations Manager - /subvendors
      const [vendorsRes, assignmentsRes] = await Promise.all([
        axios.get('/subvendors'),
        axios.get('/vendor-assignments').catch(() => ({ data: [] }))
      ]);
      setVendors(vendorsRes.data || []);
      setAssignments(assignmentsRes.data || []);
      
      // Calculate stats from vendors
      const vendorList = vendorsRes.data || [];
      setStats({
        total_vendors: vendorList.length,
        active: vendorList.filter(v => v.onboarding_status === 'active').length,
        pending: vendorList.filter(v => v.onboarding_status === 'pending' || v.onboarding_status === 'documents_pending').length,
        verified: vendorList.filter(v => v.onboarding_status === 'verified').length,
        suspended: vendorList.filter(v => v.onboarding_status === 'suspended').length
      });
    } catch (error) {
      console.error('Failed to fetch vendor data:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const resetVendorForm = () => {
    setVendorForm({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      gst_number: '',
      pan_number: '',
      specializations: [],
      service_locations: [],
      default_tat_days: 7,
      pricing: [],
      coverage_type: 'state_specific',
      coverage_states: [],
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      account_holder_name: ''
    });
    setEditingVendor(null);
  };

  const openCreateVendor = () => {
    resetVendorForm();
    setShowVendorDialog(true);
  };

  const openEditVendor = (vendor) => {
    setVendorForm({
      name: vendor.name || '',
      contact_person: vendor.contact_person || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      city: vendor.city || '',
      state: vendor.state || '',
      gst_number: vendor.gst_number || '',
      pan_number: vendor.pan_number || '',
      specializations: vendor.specializations || [],
      service_locations: vendor.service_locations || vendor.coverage_states || [],
      default_tat_days: vendor.default_tat_days || vendor.tat_days || 7,
      pricing: vendor.pricing || [],
      coverage_type: vendor.coverage_type || 'state_specific',
      coverage_states: vendor.coverage_states || [],
      bank_name: vendor.bank_details?.bank_name || '',
      account_number: vendor.bank_details?.account_number || '',
      ifsc_code: vendor.bank_details?.ifsc_code || '',
      account_holder_name: vendor.bank_details?.account_holder_name || '',
      // Preserve pricing and TAT data
      check_pricing: vendor.check_pricing || [],
      check_tat: vendor.check_tat || {},
      tat_penalties: vendor.tat_penalties || [
        { period_start: 6, period_end: 10, penalty_percent: 5 },
        { period_start: 11, period_end: 15, penalty_percent: 10 },
        { period_start: 16, period_end: 20, penalty_percent: 15 },
        { period_start: 21, period_end: null, penalty_percent: 20 }
      ]
    });
    setEditingVendor(vendor);
    setShowVendorDialog(true);
  };

  const handleSaveVendor = async () => {
    // Mandatory field validation
    if (!vendorForm.name?.trim()) {
      toast.error('Vendor Name is required');
      return;
    }
    if (!vendorForm.email?.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!vendorForm.contact_person?.trim()) {
      toast.error('Contact Person is required');
      return;
    }
    if (!vendorForm.phone?.trim()) {
      toast.error('Phone is required');
      return;
    }

    // Prepare vendor data matching Operations Manager format
    const vendorData = {
      name: vendorForm.name,
      contact_person: vendorForm.contact_person,
      email: vendorForm.email,
      phone: vendorForm.phone,
      address: vendorForm.address,
      city: vendorForm.city,
      state: vendorForm.state,
      gst_number: vendorForm.gst_number,
      pan_number: vendorForm.pan_number,
      specializations: vendorForm.specializations,
      coverage_type: vendorForm.coverage_type || 'state_specific',
      coverage_states: vendorForm.service_locations || vendorForm.coverage_states || [],
      tat_days: vendorForm.default_tat_days || 7,
      bank_details: {
        bank_name: vendorForm.bank_name || '',
        account_number: vendorForm.account_number || '',
        ifsc_code: vendorForm.ifsc_code || '',
        account_holder_name: vendorForm.account_holder_name || ''
      },
      onboarding_status: editingVendor?.onboarding_status || 'pending',
      // New fields for password and invite
      password: vendorForm.password || '',
      send_invite: vendorForm.send_invite,
    };

    // CRITICAL: Preserve existing check_pricing and check_tat when editing
    if (editingVendor) {
      vendorData.check_pricing = vendorForm.check_pricing?.length > 0 
        ? vendorForm.check_pricing 
        : editingVendor.check_pricing || [];
      vendorData.check_tat = vendorForm.check_tat && Object.keys(vendorForm.check_tat).length > 0
        ? vendorForm.check_tat
        : editingVendor.check_tat || {};
    }

    try {
      if (editingVendor) {
        await axios.put(`/subvendors/${editingVendor.id}`, vendorData);
        toast.success('Vendor updated successfully');
      } else {
        const response = await axios.post('/subvendors', vendorData);
        
        // Show credentials if invite was sent
        if (vendorForm.send_invite) {
          const password = vendorForm.password || response.data?.generated_password || 'vendor123';
          toast.success(
            <div>
              <p>Vendor created & invitation sent!</p>
              <p className="text-xs mt-1">Login: <strong>{vendorForm.email}</strong></p>
              <p className="text-xs">Password: <strong>{password}</strong></p>
            </div>,
            { duration: 10000 }
          );
        } else {
          toast.success('Vendor created successfully');
        }
      }
      setShowVendorDialog(false);
      resetVendorForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save vendor');
      toast.error(msg);
    }
  };

  // Reset password for vendor
  const handleResetVendorPassword = async (vendor) => {
    if (!confirm(`Reset password for ${vendor.vendor_name || vendor.name}?`)) return;
    
    try {
      const response = await axios.post(`/subvendors/${vendor.id}/reset-password`);
      const newPassword = response.data?.new_password || 'vendor123';
      toast.success(
        <div>
          <p>Password reset successfully!</p>
          <p className="text-xs mt-1">New Password: <strong>{newPassword}</strong></p>
          <p className="text-xs">Email sent to: {vendor.email}</p>
        </div>,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Resend invite to vendor
  const handleResendInvite = async (vendor) => {
    try {
      await axios.post(`/subvendors/${vendor.id}/resend-invite`);
      toast.success(`Invitation resent to ${vendor.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!confirm('Are you sure you want to delete this vendor?')) return;
    
    try {
      await axios.delete(`/subvendors/${vendorId}`);
      toast.success('Vendor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  // Bulk Import Functions for Vendors
  const downloadVendorTemplate = () => {
    const headers = 'name,contact_person,email,phone,address,city,state,gst_number,pan_number,specializations,coverage_states,tat_days';
    const sampleRow = 'ABC Verification Services,John Doe,contact@abcverify.com,9876543210,123 Main Street,Mumbai,Maharashtra,22AAAAA0000A1Z5,AAAAA1234A,"address_check,employment_check","Maharashtra,Gujarat",7';
    const csv = `${headers}\n${sampleRow}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vendor_import_template.csv';
    a.click();
  };

  const parseVendorCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      
      const data = [];
      const errors = [];
      
      for (let i = 1; i < lines.length; i++) {
        // Handle CSV with quoted fields
        const values = lines[i].match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
        const row = {};
        
        headers.forEach((header, idx) => {
          let value = values[idx] ? values[idx].trim().replace(/^"|"$/g, '') : '';
          row[header] = value;
        });
        
        // Validate required fields
        row.valid = !!(row.name && row.email);
        row.errors = [];
        if (!row.name) row.errors.push('Name required');
        if (!row.email) row.errors.push('Email required');
        if (row.email && !row.email.includes('@')) row.errors.push('Invalid email');
        
        // Parse arrays
        if (row.specializations) {
          row.specializations = row.specializations.split(',').map(s => s.trim()).filter(Boolean);
        } else {
          row.specializations = [];
        }
        
        if (row.coverage_states) {
          row.coverage_states = row.coverage_states.split(',').map(s => s.trim()).filter(Boolean);
        } else {
          row.coverage_states = [];
        }
        
        data.push(row);
        if (row.errors.length > 0) {
          errors.push({ row: i, errors: row.errors });
        }
      }
      
      setBulkImportData(data);
      setBulkImportErrors(errors);
    };
    reader.readAsText(file);
  };

  const handleBulkImportVendors = async () => {
    const validRecords = bulkImportData.filter(d => d.valid);
    if (validRecords.length === 0) {
      toast.error('No valid records to import');
      return;
    }

    try {
      const response = await axios.post('/subvendors/bulk-import', {
        send_invites: sendVendorInvites,
        vendors: validRecords.map(r => ({
          name: r.name,
          contact_person: r.contact_person || r.name,
          email: r.email,
          phone: r.phone || '',
          address: r.address || '',
          city: r.city || '',
          state: r.state || '',
          gst_number: r.gst_number || '',
          pan_number: r.pan_number || '',
          specializations: r.specializations || [],
          coverage_states: r.coverage_states || [],
          tat_days: parseInt(r.tat_days) || 7
        }))
      });
      
      toast.success(
        <div>
          <p>Successfully imported {response.data.imported} vendors</p>
          {sendVendorInvites && <p className="text-xs mt-1">Invitation emails sent with login credentials</p>}
        </div>,
        { duration: 8000 }
      );
      
      setShowBulkImportDialog(false);
      setBulkImportData([]);
      setBulkImportErrors([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk import failed');
    }
  };

  // Export Vendors Function
  const handleExportVendors = async () => {
    try {
      toast.info('Preparing export...');
      const response = await axios.get('/subvendors/export/csv');
      const data = response.data.data;
      
      if (data.length === 0) {
        toast.warning('No vendors to export');
        return;
      }
      
      // Build CSV content
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => {
          const val = row[h] || '';
          // Escape quotes and wrap in quotes if contains comma
          const escaped = String(val).replace(/"/g, '""');
          return escaped.includes(',') || escaped.includes('\n') ? `"${escaped}"` : escaped;
        }).join(','))
      ].join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendors_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.length} vendors`);
    } catch (error) {
      toast.error('Failed to export vendors');
      console.error(error);
    }
  };

  const handleAddPricing = async () => {
    if (!pricingForm.check_type || !pricingForm.price) {
      toast.error('Check type and price are required');
      return;
    }

    const newPricing = {
      check_type: pricingForm.check_type,
      state: pricingForm.state || 'default',
      city: pricingForm.city || '',
      price: parseFloat(pricingForm.price),
      tat_days: parseInt(pricingForm.tat_days) || 7
    };

    // Create a location key combining state and city
    const locationKey = newPricing.city 
      ? `${newPricing.state}__${newPricing.city}` 
      : newPricing.state;

    // If we're editing pricing for an existing vendor (selectedVendorForPricing),
    // save to the database
    if (selectedVendorForPricing) {
      try {
        // Get existing check_pricing - ensure it's a proper object
        let existingPricing = {};
        const currentPricing = selectedVendorForPricing.check_pricing;
        
        // Validate and convert existing pricing
        if (currentPricing && typeof currentPricing === 'object') {
          // Check if it's in the correct nested structure
          for (const [key, value] of Object.entries(currentPricing)) {
            // Skip if the value is not an object or doesn't have proper location structure
            if (value && typeof value === 'object' && !Array.isArray(value)) {
              // Check if it's a valid location->config structure
              const hasValidLocations = Object.values(value).some(
                loc => loc && typeof loc === 'object' && ('price' in loc || 'base_price' in loc)
              );
              if (hasValidLocations) {
                existingPricing[key] = value;
              }
            }
          }
        }

        // EDIT mode (May 24, 2026): drop the original row before
        // writing the new one. This also handles the case where the
        // admin changes State or City — old key gets cleaned up.
        if (editingPricingRow) {
          const oct = editingPricingRow.check_type;
          const oloc = editingPricingRow.location;
          if (existingPricing[oct] && existingPricing[oct][oloc]) {
            delete existingPricing[oct][oloc];
            if (Object.keys(existingPricing[oct]).length === 0) {
              delete existingPricing[oct];
            }
          }
        }

        // Structure: { check_type: { location_key: { state, city, price, tat_days } } }
        if (!existingPricing[newPricing.check_type]) {
          existingPricing[newPricing.check_type] = {};
        }
        // Duplicate guard — bail out when (check_type, location_key)
        // already exists and we're NOT in edit mode (e.g. accidentally
        // re-adding a mirrored Permanent address row that's already
        // captured by Present).
        if (!editingPricingRow && existingPricing[newPricing.check_type][locationKey]) {
          toast.error('Duplicate: pricing for this (check type + state + city/pincode) already exists. Use the Edit button on that row instead.');
          return;
        }
        existingPricing[newPricing.check_type][locationKey] = {
          state: newPricing.state,
          city: newPricing.city,
          price: newPricing.price,
          tat_days: newPricing.tat_days
        };
        
        // Convert to array format for API
        const checkPricingArray = [];
        for (const [checkType, locations] of Object.entries(existingPricing)) {
          for (const [locKey, config] of Object.entries(locations)) {
            if (config && typeof config === 'object') {
              checkPricingArray.push({
                check_type: checkType,
                location: locKey,
                state: config.state || locKey.split('__')[0] || 'default',
                city: config.city || locKey.split('__')[1] || '',
                base_price: config.price || config.base_price || 0,
                tat_days: config.tat_days || 7
              });
            }
          }
        }
        
        await axios.post(`/subvendors/${selectedVendorForPricing.id}/pricing`, {
          check_pricing: checkPricingArray
        });
        
        // Update local state
        setSelectedVendorForPricing({
          ...selectedVendorForPricing,
          check_pricing: existingPricing
        });
        
        // Update vendors list
        setVendors(prev => prev.map(v => 
          v.id === selectedVendorForPricing.id 
            ? { ...v, check_pricing: existingPricing }
            : v
        ));
        
        toast.success(editingPricingRow ? 'Pricing updated' : 'Pricing added successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save pricing');
        return;
      }
    } else {
      // Adding to new vendor form
      setVendorForm(prev => ({
        ...prev,
        pricing: [...prev.pricing, newPricing]
      }));
    }

    setPricingForm({
      check_type: '',
      state: 'default',
      city: '',
      price: '',
      tat_days: 7
    });
    setEditingPricingRow(null);
    setShowPricingDialog(false);
  };

  const handleDeleteVendorPricing = async (checkType, location) => {
    if (!selectedVendorForPricing) return;
    
    try {
      const existingPricing = { ...selectedVendorForPricing.check_pricing };
      
      // Remove the specific pricing
      if (existingPricing[checkType]) {
        delete existingPricing[checkType][location];
        // If no more locations for this check type, remove the check type
        if (Object.keys(existingPricing[checkType]).length === 0) {
          delete existingPricing[checkType];
        }
      }
      
      // Convert to array format for API
      const checkPricingArray = [];
      for (const [ct, locations] of Object.entries(existingPricing)) {
        for (const [loc, config] of Object.entries(locations)) {
          checkPricingArray.push({
            check_type: ct,
            location: loc,
            base_price: config.price,
            tat_days: config.tat_days
          });
        }
      }
      
      await axios.post(`/api/subvendors/${selectedVendorForPricing.id}/pricing`, {
        check_pricing: checkPricingArray
      });
      
      // Update local state
      setSelectedVendorForPricing({
        ...selectedVendorForPricing,
        check_pricing: existingPricing
      });
      
      // Update vendors list
      setVendors(prev => prev.map(v => 
        v.id === selectedVendorForPricing.id 
          ? { ...v, check_pricing: existingPricing }
          : v
      ));
      
      toast.success('Pricing removed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove pricing');
    }
  };

  const handleRemovePricing = (index) => {
    setVendorForm(prev => ({
      ...prev,
      pricing: prev.pricing.filter((_, i) => i !== index)
    }));
  };

  const toggleSpecialization = (checkType) => {
    setVendorForm(prev => ({
      ...prev,
      specializations: prev.specializations.includes(checkType)
        ? prev.specializations.filter(s => s !== checkType)
        : [...prev.specializations, checkType]
    }));
  };

  const toggleServiceLocation = (location) => {
    setVendorForm(prev => ({
      ...prev,
      service_locations: prev.service_locations.includes(location)
        ? prev.service_locations.filter(l => l !== location)
        : [...prev.service_locations, location]
    }));
  };

  const getCheckTypeName = (checkType) => {
    const check = CHECK_TYPES.find(c => c.id === checkType);
    return check ? check.name : checkType?.replace(/_/g, ' ').toUpperCase();
  };

  // Filter vendors by status, search, and sort
  const filteredVendors = vendors.filter(v => {
    // Status filter
    if (vendorFilter !== 'all') {
      if (vendorFilter === 'pending') {
        if (v.onboarding_status !== 'pending' && v.onboarding_status !== 'documents_pending') return false;
      } else if (v.onboarding_status !== vendorFilter) return false;
    }
    // Search filter
    if (vendorSearch) {
      const search = vendorSearch.toLowerCase();
      return (
        (v.name || '').toLowerCase().includes(search) ||
        (v.email || '').toLowerCase().includes(search) ||
        (v.contact_person || '').toLowerCase().includes(search) ||
        (v.phone || '').includes(search) ||
        (v.city || '').toLowerCase().includes(search) ||
        (v.state || '').toLowerCase().includes(search) ||
        (v.gst_number || '').toLowerCase().includes(search)
      );
    }
    return true;
  }).sort((a, b) => {
    switch (vendorSort) {
      case 'name_asc': return (a.name || '').localeCompare(b.name || '');
      case 'name_desc': return (b.name || '').localeCompare(a.name || '');
      case 'date_desc': return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'date_asc': return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      default: return (a.name || '').localeCompare(b.name || '');
    }
  });

  // Handler functions for vendor management
  const handleOnboardVendor = async (vendorId, newStatus) => {
    try {
      await axios.put(`/subvendors/${vendorId}`, { onboarding_status: newStatus });
      toast.success(`Vendor status updated to ${ONBOARDING_STATUSES[newStatus]?.label || newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update vendor status');
    }
  };

  const handleSaveContacts = async () => {
    if (!selectedVendorForContacts) return;
    try {
      await axios.put(`/subvendors/${selectedVendorForContacts.id}`, {
        additional_contacts: selectedVendorForContacts.additional_contacts || []
      });
      toast.success('Contacts updated');
      setShowContactsDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update contacts');
    }
  };

  const handleSaveTat = async (tatConfig) => {
    if (!selectedVendorForTat) return;
    try {
      await axios.put(`/subvendors/${selectedVendorForTat.id}`, {
        check_tat: tatConfig
      });
      toast.success('TAT configuration saved');
      setShowTatDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save TAT configuration');
    }
  };

  const openContactsDialog = (vendor) => {
    setSelectedVendorForContacts(vendor);
    setShowContactsDialog(true);
  };

  const openTatDialog = (vendor) => {
    setSelectedVendorForTat(vendor);
    setShowTatDialog(true);
  };

  const filteredAssignments = assignments.filter(a => {
    if (assignmentFilter === 'all') return true;
    return a.status === assignmentFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-teal-700">Vendor Management</h2>
          <p className="text-slate-500 text-sm">Onboard and manage verification vendors</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowImportDialog(true)}
            variant="outline"
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Vendors
          </Button>
          <Button 
            onClick={handleExportVendors}
            variant="outline"
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            data-testid="export-vendors-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Vendors
          </Button>
          <Button 
            onClick={() => { resetVendorForm(); setEditingVendor(null); setShowVendorDialog(true); }}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="add-vendor-btn"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Add New Vendor
          </Button>
        </div>
      </div>

      {/* Stats Cards - Operations Manager Style */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-slate-50 border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setVendorFilter('all')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-700">{vendors.length}</div>
            <div className="text-xs text-slate-500">Total Vendors</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setVendorFilter('active')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-700">
              {vendors.filter(v => v.onboarding_status === 'active').length}
            </div>
            <div className="text-xs text-green-600">Active</div>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setVendorFilter('pending')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-700">
              {vendors.filter(v => v.onboarding_status === 'pending' || v.onboarding_status === 'documents_pending').length}
            </div>
            <div className="text-xs text-yellow-600">Pending</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setVendorFilter('verified')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-700">
              {vendors.filter(v => v.onboarding_status === 'verified').length}
            </div>
            <div className="text-xs text-blue-600">Verified</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setVendorFilter('suspended')}>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-700">
              {vendors.filter(v => v.onboarding_status === 'suspended').length}
            </div>
            <div className="text-xs text-red-600">Suspended</div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="vendors">Vendors ({vendors.length})</TabsTrigger>
          <TabsTrigger value="assignments">Assignments ({assignments.length})</TabsTrigger>
          <TabsTrigger value="invoices">
            <Receipt className="w-4 h-4 mr-1" />
            Invoices
          </TabsTrigger>
        </TabsList>

        {/* Vendors Tab - Table Layout */}
        <TabsContent value="vendors" className="space-y-4">
          {/* Filter Badge */}
          {vendorFilter !== 'all' && (
            <div className="flex items-center gap-2">
              <Badge className={ONBOARDING_STATUSES[vendorFilter]?.color || 'bg-gray-100'}>
                Showing: {ONBOARDING_STATUSES[vendorFilter]?.label || vendorFilter}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => setVendorFilter('all')}>
                <X className="w-4 h-4" /> Clear
              </Button>
            </div>
          )}

          {/* Search & Sort Controls */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search vendors by name, email, phone, city, state, GST..."
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                data-testid="vendor-search-input"
              />
            </div>
            <select
              value={vendorSort}
              onChange={(e) => setVendorSort(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="vendor-sort-select"
            >
              <option value="name_asc">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
            </select>
            <span className="text-sm text-gray-500">{filteredVendors.length} vendor(s)</span>
          </div>

          {/* Vendors Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-teal-50">
                    <TableHead>Vendor Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Coverage</TableHead>
                    <TableHead>Specializations</TableHead>
                    <TableHead>TAT</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Performance</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVendors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                        <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No vendors found</p>
                        <Button onClick={openCreateVendor} variant="outline" className="mt-4">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Your First Vendor
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredVendors.map((vendor) => (
                      <TableRow key={vendor.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div>
                            <p className="font-semibold">{vendor.name}</p>
                            <p className="text-xs text-slate-500">{vendor.contact_person}</p>
                            {vendor.gst_number && (
                              <p className="text-xs text-blue-600">GST: {vendor.gst_number}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="flex items-center gap-1">
                              <Mail className="w-3 h-3 text-slate-400" />
                              {vendor.email}
                            </p>
                            <p className="text-slate-500 flex items-center gap-1">
                              <Phone className="w-3 h-3 text-slate-400" />
                              {vendor.phone || 'N/A'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {vendor.coverage_type === 'pan_india' ? 'Pan India' : 
                             vendor.service_locations?.length ? `${vendor.service_locations.length} States` : 
                             vendor.coverage_states?.length ? `${vendor.coverage_states.length} States` : 'Not Set'}
                          </Badge>
                          {(vendor.service_locations?.length > 0 || vendor.coverage_states?.length > 0) && vendor.coverage_type !== 'pan_india' && (
                            <p className="text-xs text-slate-500 mt-1 truncate max-w-[150px]" 
                               title={(vendor.service_locations || vendor.coverage_states || []).join(', ')}>
                              {(vendor.service_locations || vendor.coverage_states || []).slice(0, 3).join(', ')}
                              {(vendor.service_locations || vendor.coverage_states || []).length > 3 ? '...' : ''}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(vendor.specializations || []).slice(0, 2).map(spec => (
                              <Badge key={spec} variant="outline" className="text-xs bg-slate-50">
                                {getCheckTypeName(spec)}
                              </Badge>
                            ))}
                            {(vendor.specializations || []).length > 2 && (
                              <Badge variant="outline" className="text-xs">+{vendor.specializations.length - 2}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-blue-100 text-blue-800">{vendor.default_tat_days || vendor.tat_days || 7}d</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={ONBOARDING_STATUSES[vendor.onboarding_status]?.color || 'bg-gray-100 text-gray-800'}>
                            {ONBOARDING_STATUSES[vendor.onboarding_status]?.label || vendor.onboarding_status || 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <p>Assigned: <span className="font-semibold">{vendor.total_assigned || 0}</span></p>
                            <p>Completed: <span className="font-semibold text-green-600">{vendor.total_completed || 0}</span></p>
                            <p>On-time: <span className="font-semibold">{vendor.on_time_delivery_rate || 100}%</span></p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end flex-wrap">
                            <Button variant="outline" size="sm" onClick={() => openEditVendor(vendor)} title="Edit">
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openContactsDialog(vendor)} 
                              title="Manage Contacts"
                              className="text-indigo-600"
                            >
                              <Users className="w-4 h-4" />
                              {vendor.additional_contacts?.length > 0 && (
                                <span className="ml-1 text-xs">{vendor.additional_contacts.length + 1}</span>
                              )}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => {
                                setSelectedVendorForPricing(vendor);
                                setShowVendorPricingDialog(true);
                              }} 
                              title="Configure Pricing"
                              className="text-green-600"
                            >
                              <DollarSign className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openTatDialog(vendor)} 
                              title="Configure TAT per Check"
                              className="text-orange-600"
                            >
                              <Clock className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => {
                                setSelectedVendorForPenalty(vendor);
                                setShowPenaltyDialog(true);
                              }} 
                              title="Configure TAT Penalties"
                              className="text-rose-600"
                            >
                              <Percent className="w-4 h-4" />
                            </Button>
                            {vendor.onboarding_status === 'pending' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-blue-600"
                                onClick={() => handleOnboardVendor(vendor.id, 'verified')}
                                title="Verify"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {vendor.onboarding_status === 'verified' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-green-600"
                                onClick={() => handleOnboardVendor(vendor.id, 'active')}
                                title="Activate"
                              >
                                <Zap className="w-4 h-4" />
                              </Button>
                            )}
                            {vendor.onboarding_status === 'active' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-600"
                                onClick={() => handleOnboardVendor(vendor.id, 'suspended')}
                                title="Suspend"
                              >
                                <AlertTriangle className="w-4 h-4" />
                              </Button>
                            )}
                            {vendor.onboarding_status === 'suspended' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-green-600"
                                onClick={() => handleOnboardVendor(vendor.id, 'active')}
                                title="Reactivate"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleResetVendorPassword(vendor)}
                              title="Reset Password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleResendInvite(vendor)}
                              title="Resend Invite"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-rose-600" 
                              onClick={() => handleDeleteVendor(vendor.id)}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-4">
          <div className="flex items-center gap-4">
            <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignments</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Assignment ID</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Check Type</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead>Expected</TableHead>
                    <TableHead>TAT Status</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssignments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        No assignments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAssignments.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell className="font-mono text-sm">{assignment.id}</TableCell>
                        <TableCell className="font-medium">{assignment.vendor_name || 'Unknown'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getCheckTypeName(assignment.check_type)}</Badge>
                        </TableCell>
                        <TableCell>{assignment.employee_name || '-'}</TableCell>
                        <TableCell>{assignment.client_name || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {assignment.assigned_at ? new Date(assignment.assigned_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {assignment.expected_completion ? new Date(assignment.expected_completion).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          {assignment.status === 'completed' ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                          ) : assignment.tat_status === 'delayed' ? (
                            <Badge className="bg-rose-100 text-rose-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Delayed ({Math.abs(assignment.days_remaining)}d)
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <Clock className="w-3 h-3 mr-1" />
                              {assignment.days_remaining}d left
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {assignment.status === 'assigned' && (
                            <Badge className="bg-blue-100 text-blue-700">Assigned</Badge>
                          )}
                          {assignment.status === 'in_progress' && (
                            <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>
                          )}
                          {assignment.status === 'completed' && (
                            <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {assignment.result === 'green' && (
                            <Badge className="bg-emerald-100 text-emerald-700">GREEN</Badge>
                          )}
                          {assignment.result === 'amber' && (
                            <Badge className="bg-amber-100 text-amber-700">AMBER</Badge>
                          )}
                          {assignment.result === 'red' && (
                            <Badge className="bg-rose-100 text-rose-700">RED</Badge>
                          )}
                          {!assignment.result && '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <VendorInvoices vendors={vendors} />
        </TabsContent>
      </Tabs>

      {/* Vendor Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor Name *</Label>
                <Input 
                  value={vendorForm.name}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="ABC Verifications Pvt Ltd"
                />
              </div>
              <div>
                <Label>Contact Person *</Label>
                <Input 
                  value={vendorForm.contact_person}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, contact_person: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={vendorForm.email}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="vendor@company.com"
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input 
                  value={vendorForm.phone}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+91 9876543210"
                />
              </div>
            </div>

            {/* Address */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-3">
                <Label>Address</Label>
                <Textarea 
                  value={vendorForm.address}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Full address"
                  rows={2}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input 
                  value={vendorForm.city}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Mumbai"
                />
              </div>
              <div>
                <Label>State</Label>
                <Select value={vendorForm.state} onValueChange={(v) => setVendorForm(prev => ({ ...prev, state: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDIAN_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default TAT (Days)</Label>
                <Input 
                  type="number"
                  value={vendorForm.default_tat_days}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, default_tat_days: parseInt(e.target.value) || 7 }))}
                />
              </div>
            </div>

            {/* Tax Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GST Number</Label>
                <Input 
                  value={vendorForm.gst_number}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, gst_number: e.target.value }))}
                  placeholder="22AAAAA0000A1Z5"
                />
              </div>
              <div>
                <Label>PAN Number</Label>
                <Input 
                  value={vendorForm.pan_number}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, pan_number: e.target.value }))}
                  placeholder="ABCDE1234F"
                />
              </div>
            </div>

            {/* Bank Details */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Bank Details</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Bank Name</Label>
                  <Input 
                    value={vendorForm.bank_name}
                    onChange={(e) => setVendorForm(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="HDFC Bank"
                  />
                </div>
                <div>
                  <Label className="text-sm">Account Holder Name</Label>
                  <Input 
                    value={vendorForm.account_holder_name}
                    onChange={(e) => setVendorForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                    placeholder="Account holder name"
                  />
                </div>
                <div>
                  <Label className="text-sm">Account Number</Label>
                  <Input 
                    value={vendorForm.account_number}
                    onChange={(e) => setVendorForm(prev => ({ ...prev, account_number: e.target.value }))}
                    placeholder="1234567890"
                  />
                </div>
                <div>
                  <Label className="text-sm">IFSC Code</Label>
                  <Input 
                    value={vendorForm.ifsc_code}
                    onChange={(e) => setVendorForm(prev => ({ ...prev, ifsc_code: e.target.value }))}
                    placeholder="HDFC0001234"
                  />
                </div>
              </div>
            </div>

            {/* Coverage Type */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Coverage Type</Label>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant={vendorForm.coverage_type === 'pan_india' ? "default" : "outline"}
                  size="sm"
                  className={vendorForm.coverage_type === 'pan_india' ? "bg-teal-600" : ""}
                  onClick={() => setVendorForm(prev => ({ ...prev, coverage_type: 'pan_india' }))}
                >
                  Pan India
                </Button>
                <Button
                  type="button"
                  variant={vendorForm.coverage_type === 'state_specific' ? "default" : "outline"}
                  size="sm"
                  className={vendorForm.coverage_type === 'state_specific' ? "bg-teal-600" : ""}
                  onClick={() => setVendorForm(prev => ({ ...prev, coverage_type: 'state_specific' }))}
                >
                  State Specific
                </Button>
              </div>
            </div>

            {/* Specializations */}
            <div>
              <Label>Specializations (Check Types)</Label>
              <div className="flex flex-wrap gap-2 mt-2 p-3 bg-slate-50 rounded-lg">
                {CHECK_TYPES.map((check) => (
                  <Button
                    key={check.id}
                    type="button"
                    variant={vendorForm.specializations.includes(check.id) ? "default" : "outline"}
                    size="sm"
                    className={vendorForm.specializations.includes(check.id) ? "bg-indigo-600" : ""}
                    onClick={() => toggleSpecialization(check.id)}
                  >
                    {check.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Service Locations - Only show if state_specific */}
            {vendorForm.coverage_type === 'state_specific' && (
              <div>
                <Label>Coverage States (Select states this vendor covers)</Label>
                <div className="flex flex-wrap gap-2 mt-2 p-3 bg-slate-50 rounded-lg max-h-40 overflow-y-auto">
                  {INDIAN_STATES.filter(s => s !== 'All India').map((location) => (
                    <Button
                      key={location}
                      type="button"
                      variant={vendorForm.service_locations.includes(location) ? "default" : "outline"}
                      size="sm"
                      className={vendorForm.service_locations.includes(location) ? "bg-emerald-600" : ""}
                      onClick={() => toggleServiceLocation(location)}
                    >
                      {location}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Selected: {vendorForm.service_locations.length} states
                </p>
              </div>
            )}

            {/* Pricing */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Pricing Configuration</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowPricingDialog(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Pricing
                </Button>
              </div>
              {vendorForm.pricing.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Check Type</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Price (₹)</TableHead>
                      <TableHead>TAT (Days)</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendorForm.pricing.map((p, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{getCheckTypeName(p.check_type)}</TableCell>
                        <TableCell>{p.location}</TableCell>
                        <TableCell>₹{p.price}</TableCell>
                        <TableCell>{p.tat_days} days</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleRemovePricing(idx)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-4 bg-slate-50 rounded-lg text-slate-500 text-sm">
                  No pricing configured. Click "Add Pricing" to set rates.
                </div>
              )}
            </div>
            
            {/* Login Credentials Section - Only for new vendors */}
            {!editingVendor && (
              <div className="border rounded-lg p-4 bg-blue-50">
                <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Portal Login Credentials
                </h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Password</Label>
                    <Input 
                      type="text"
                      value={vendorForm.password} 
                      onChange={(e) => setVendorForm({ ...vendorForm, password: e.target.value })} 
                      placeholder="Enter password or leave blank for default (vendor123)"
                    />
                    <p className="text-xs text-slate-500 mt-1">Login email: {vendorForm.email || 'vendor email'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="send_invite"
                      checked={vendorForm.send_invite}
                      onCheckedChange={(checked) => setVendorForm({ ...vendorForm, send_invite: checked })}
                    />
                    <label htmlFor="send_invite" className="text-sm cursor-pointer">
                      Send invitation email with login credentials
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveVendor} className="bg-indigo-600 hover:bg-indigo-700">
              {editingVendor ? 'Update Vendor' : 'Create Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={(open) => { setShowPricingDialog(open); if (!open) setEditingPricingRow(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              {editingPricingRow ? 'Edit Pricing' : 'Add Pricing'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Check Type *</Label>
              <Select value={pricingForm.check_type} onValueChange={(v) => setPricingForm(prev => ({ ...prev, check_type: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select check type" />
                </SelectTrigger>
                <SelectContent>
                  {CHECK_TYPES.map(check => (
                    <SelectItem key={check.id} value={check.id}>{check.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>State</Label>
                <Select value={pricingForm.state} onValueChange={(v) => setPricingForm(prev => ({ ...prev, state: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">All States</SelectItem>
                    {INDIAN_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>City/Location / Pincode Prefix</Label>
                <Input 
                  value={pricingForm.city}
                  onChange={(e) => setPricingForm(prev => ({ ...prev, city: e.target.value }))}
                  placeholder='e.g. "Chennai" or "6000" (matches pincodes starting with 6000)'
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter a city name (e.g. <code>Chennai</code>) OR a numeric pincode prefix (e.g. <code>6000</code>) — the latter matches any address pincode starting with those digits.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹) *</Label>
                <Input 
                  type="number"
                  value={pricingForm.price}
                  onChange={(e) => setPricingForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="500"
                />
              </div>
              <div>
                <Label>TAT (Days)</Label>
                <Input 
                  type="number"
                  value={pricingForm.tat_days}
                  onChange={(e) => setPricingForm(prev => ({ ...prev, tat_days: parseInt(e.target.value) || 7 }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPricingDialog(false); setEditingPricingRow(null); }}>Cancel</Button>
            <Button onClick={handleAddPricing} className="bg-emerald-600 hover:bg-emerald-700">
              {editingPricingRow ? 'Save Changes' : 'Add Pricing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TAT Configuration Dialog */}
      <Dialog open={showTatDialog} onOpenChange={setShowTatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              TAT Configuration - {selectedVendorForTat?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Configure Turn-Around Time (TAT) for each check type. Leave empty to use vendor's default TAT.
            </p>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {CHECK_TYPES.map((check) => (
                <div key={check.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="text-sm">{check.name}</span>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number" 
                      className="w-20 h-8 text-sm"
                      placeholder={`${selectedVendorForTat?.default_tat_days || 7}`}
                      defaultValue={selectedVendorForTat?.check_tat?.[check.id] || ''}
                    />
                    <span className="text-xs text-slate-500">days</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTatDialog(false)}>Cancel</Button>
            <Button onClick={() => { toast.success('TAT configuration saved'); setShowTatDialog(false); }} className="bg-orange-600 hover:bg-orange-700">
              Save TAT Config
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contacts Management Dialog */}
      <Dialog open={showContactsDialog} onOpenChange={setShowContactsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Manage Contacts - {selectedVendorForContacts?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-indigo-50 rounded-lg">
              <p className="text-sm font-medium text-indigo-800">Primary Contact</p>
              <p className="text-sm">{selectedVendorForContacts?.contact_person || 'N/A'}</p>
              <p className="text-xs text-slate-600">{selectedVendorForContacts?.email}</p>
              <p className="text-xs text-slate-600">{selectedVendorForContacts?.phone}</p>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Additional Contacts</Label>
                <Button variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Contact
                </Button>
              </div>
              {(selectedVendorForContacts?.additional_contacts || []).length === 0 ? (
                <div className="text-center py-4 bg-slate-50 rounded-lg text-slate-500 text-sm">
                  No additional contacts configured
                </div>
              ) : (
                <div className="space-y-2">
                  {(selectedVendorForContacts?.additional_contacts || []).map((contact, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div>
                        <p className="text-sm font-medium">{contact.name}</p>
                        <p className="text-xs text-slate-600">{contact.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {contact.cc_on_emails && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">CC</Badge>
                        )}
                        <Button variant="ghost" size="sm" className="text-rose-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Pricing Dialog */}
      <Dialog open={showVendorPricingDialog} onOpenChange={setShowVendorPricingDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Pricing Configuration - {selectedVendorForPricing?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Configure pricing for each check type by state and city/location. Default pricing applies unless overridden.
            </p>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Check Type</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>City/Location / Pincode Prefix</TableHead>
                  <TableHead>Price (₹)</TableHead>
                  <TableHead>TAT</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const cp = selectedVendorForPricing?.check_pricing;
                  // Normalize pricing to array of display rows
                  let pricingRows = [];
                  if (Array.isArray(cp)) {
                    // Array format from API: [{check_type, base_price, tat_days, state, city, location}]
                    pricingRows = cp.map(item => ({
                      check_type: item.check_type || 'unknown',
                      location: item.location || item.state || 'default',
                      state: item.state || item.location?.split('__')[0] || 'default',
                      city: item.city || item.location?.split('__')[1] || '',
                      price: item.base_price || item.price || 0,
                      tat_days: item.tat_days || 7
                    }));
                  } else if (cp && typeof cp === 'object') {
                    // Object format: {check_type: {location: {price, tat_days, state, city}}}
                    pricingRows = Object.entries(cp).flatMap(([checkType, locations]) => {
                      if (!locations || typeof locations !== 'object') return [];
                      return Object.entries(locations).map(([location, config]) => ({
                        check_type: checkType,
                        location,
                        state: config?.state || location.split('__')[0] || 'default',
                        city: config?.city || location.split('__')[1] || '',
                        price: config?.price || config?.base_price || 0,
                        tat_days: config?.tat_days || 7
                      }));
                    });
                  }
                  
                  if (pricingRows.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No pricing configured for this vendor
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  return pricingRows.map((row, idx) => (
                    <TableRow key={`${row.check_type}-${row.location}-${idx}`}>
                      <TableCell>{getCheckTypeName(row.check_type)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.state === 'default' || !row.state ? 'All States' : row.state}</Badge>
                      </TableCell>
                      <TableCell>{row.city || '-'}</TableCell>
                      <TableCell>₹{row.price}</TableCell>
                      <TableCell>{row.tat_days} days</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`edit-pricing-${row.check_type}-${row.location}`}
                            title="Edit this pricing row"
                            onClick={() => {
                              setEditingPricingRow({
                                check_type: row.check_type,
                                location: row.location,
                              });
                              setPricingForm({
                                check_type: row.check_type,
                                state: row.state || 'default',
                                city: row.city || '',
                                price: String(row.price ?? ''),
                                tat_days: row.tat_days || 7,
                              });
                              setShowPricingDialog(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => handleDeleteVendorPricing(row.check_type, row.location)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
            
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => setShowPricingDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add New Pricing
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorPricingDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Vendors Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) {
          setImportFile(null);
          setBulkImportData([]);
          setBulkImportErrors([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="vendor-bulk-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-600" />
              Bulk Import Vendors
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Upload a CSV file to bulk import vendors. Download the template for correct formatting.
            </p>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={downloadVendorTemplate} data-testid="download-vendor-template-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <Input
                type="file"
                accept=".csv"
                data-testid="vendor-import-file-input"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setImportFile(file);
                    parseVendorCSV(file);
                  }
                }}
                className="mb-2"
              />
              <p className="text-xs text-slate-500">Supported format: CSV</p>
            </div>
            
            {importFile && (
              <div className="bg-teal-50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <FileUp className="w-5 h-5 text-teal-600" />
                  <span className="font-medium text-teal-800">{importFile.name}</span>
                  <Badge className="ml-auto">{bulkImportData.length} records</Badge>
                </div>
              </div>
            )}

            {/* Validation Errors */}
            {bulkImportErrors.length > 0 && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                <p className="text-sm font-medium text-rose-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Validation Errors ({bulkImportErrors.length})
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {bulkImportErrors.map((error, idx) => (
                    <p key={idx} className="text-xs text-rose-600">
                      Row {error.row}: {error.errors?.join(', ') || 'Invalid data'}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Table */}
            {bulkImportData.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b">
                  <p className="text-sm font-medium">
                    Preview: {bulkImportData.filter(d => d.valid).length} valid / {bulkImportData.length} total
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Vendor Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkImportData.slice(0, 10).map((row, idx) => (
                        <TableRow key={idx} className={row.valid ? '' : 'bg-rose-50'}>
                          <TableCell className="text-slate-500">{idx + 1}</TableCell>
                          <TableCell className="font-medium">{row.name || '-'}</TableCell>
                          <TableCell className="text-sm">{row.email || '-'}</TableCell>
                          <TableCell className="text-sm">{row.phone || '-'}</TableCell>
                          <TableCell className="text-sm">{row.state || '-'}</TableCell>
                          <TableCell>
                            {row.valid ? (
                              <Badge className="bg-green-100 text-green-700">Valid</Badge>
                            ) : (
                              <Badge className="bg-rose-100 text-rose-700">Invalid</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {bulkImportData.length > 10 && (
                    <p className="text-xs text-slate-500 text-center py-2">
                      Showing 10 of {bulkImportData.length} records...
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Send Invites Checkbox */}
            {bulkImportData.filter(d => d.valid).length > 0 && (
              <div className="flex items-center gap-2 py-2">
                <Checkbox
                  id="send-vendor-invites"
                  checked={sendVendorInvites}
                  onCheckedChange={(checked) => setSendVendorInvites(checked)}
                  data-testid="send-vendor-invites-checkbox"
                />
                <Label htmlFor="send-vendor-invites" className="text-sm cursor-pointer">
                  Send invitation emails with login credentials
                </Label>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              setShowImportDialog(false);
              setImportFile(null);
              setBulkImportData([]);
              setBulkImportErrors([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkImportVendors}
              className="bg-teal-600 hover:bg-teal-700"
              disabled={bulkImportData.filter(d => d.valid).length === 0}
              data-testid="confirm-vendor-import-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import {bulkImportData.filter(d => d.valid).length} Vendors
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TAT Penalty Dialog */}
      <Dialog open={showPenaltyDialog} onOpenChange={setShowPenaltyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-rose-600" />
              TAT Penalty Configuration - {selectedVendorForPenalty?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Configure penalty percentages for TAT breaches. Penalties are calculated based on days exceeded past the agreed TAT.
            </p>
            
            <div className="space-y-3">
              {(selectedVendorForPenalty?.tat_penalties || [
                { period_start: 6, period_end: 10, penalty_percent: 5 },
                { period_start: 11, period_end: 15, penalty_percent: 10 },
                { period_start: 16, period_end: 20, penalty_percent: 15 },
                { period_start: 21, period_end: null, penalty_percent: 20 }
              ]).map((penalty, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-700 min-w-[100px]">
                        {penalty.period_end ? `${penalty.period_start}-${penalty.period_end} days` : `${penalty.period_start}+ days`}
                      </span>
                      <span className="text-xs text-slate-500">delay period</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={penalty.penalty_percent}
                      onChange={(e) => {
                        toast.info('Update penalty to ' + e.target.value + '%');
                      }}
                      className="w-16 h-8 text-center"
                    />
                    <span className="text-sm font-medium text-rose-600">%</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-rose-50 rounded-lg p-3 text-sm">
              <p className="text-rose-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Penalties will be automatically deducted from vendor invoices based on TAT breach duration.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowPenaltyDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                toast.success('TAT Penalty configuration saved');
                setShowPenaltyDialog(false);
              }} 
              className="bg-rose-600 hover:bg-rose-700"
            >
              Save Penalties
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
